import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  SyntheticEvent,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { stringify } from 'query-string'
import { useWindowWidth } from '@react-hook/window-size'
import { breadcrumbTransformer } from 'src/models/Breadcrumb/breadcrumbTransformer'
import { colors } from 'src/theme'
import {
  TcClickTypes,
  TcPageTemplateNames,
  tcRecipeFilterTitle,
} from 'src/configuration/tagcommander'
import { tagCommanderTransformer } from 'src/models/TagCommander/tagCommanderTransformer'
import tcClick from 'src/models/tcClick/tcClick'
import { useRouter } from 'next/router'

import { secureSanitize } from '../utils/sanitizeUtils'
import Router from '../routes/router'
import routes from '../routes/definitions'
import {
  recipesSeoTransformer,
  recipesCoverTransformer,
  recipesHighlightTransformer,
  recipesListTransformer,
} from '../models/Recipes'
import { selectors } from '../redux'
import { SettingsState } from '../redux/settings/redux'
import { RecipesState, actions as RecipesActions } from '../redux/recipes/redux'
import { RecipesTemplateProps } from '../templates/Recipes'
import sizes from '../theme/sizes'

import usePWAInstallPrompt from './usePWAInstallPrompt'

const useRecipes = () => {
  usePWAInstallPrompt()
  const recipes: RecipesState['list'] = useSelector(selectors.recipes.list)
  const currentPage: RecipesState['page'] = useSelector(selectors.recipes.page)
  const storeFilters: RecipesState['filters'] = useSelector(
    selectors.recipes.filters
  )
  const settings: SettingsState['list'] = useSelector(selectors.settings.list)
  const hasPreviousPages = useSelector(selectors.recipes.hasPreviousPages)
  const hasMorePages = useSelector(selectors.recipes.hasMorePages)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  console.log('currentPage', currentPage)

  const [filters, setFilters] = useState<{ [key: string]: any }>(storeFilters)
  const lastRecipeFilteredQuery = useRef(filters.query)
  const onlyWidth = useWindowWidth()
  const isMobile = onlyWidth < sizes.tablet
  const nextRouter = useRouter()

  // Using the improved sanitization utility - decode first, then sanitize
  const rawQueryFromUrl = nextRouter.query?.query as string
  const queryParameter = secureSanitize(rawQueryFromUrl, true)

  const resultsRef = useRef<HTMLDivElement>(null)

  console.log('nextRouter.query', nextRouter.query)
  console.log('nextRouter.query.query', nextRouter.query.query)
  console.log('queryParameter', queryParameter)

  const currentTcTemplate = useMemo(
    () =>
      queryParameter
        ? TcPageTemplateNames.RecipeSearch
        : TcPageTemplateNames.Recipe,
    [queryParameter]
  )

  const handleFilterChange = useCallback(
    (field, values) => {
      resultsRef.current?.scrollIntoView?.({ behavior: 'smooth' })
      setFilters((prev) => ({ ...prev, [field]: values }))
    },
    [setFilters]
  )
  const setQuery = useCallback(
    (query) => {
      // Sanitize any user input from search
      const sanitizedQuery = secureSanitize(query)
      handleFilterChange('query', sanitizedQuery || null)
    },
    [handleFilterChange]
  )

  const handleApplyFilter = useCallback(
    (ev?: SyntheticEvent) => {
      dispatch(RecipesActions?.setRecipesFilters(filters))
      dispatch(RecipesActions?.setRecipesPage(1))
      dispatch(RecipesActions?.loadRecipes())
      ev &&
        tcClick({
          click_name: `${t(
            'recipes_filters_apply_cta'
          )}_filtre_${currentTcTemplate}_fam`,
          click_type: TcClickTypes.action,
          chapter1: `action_${currentTcTemplate}_fam`,
          chapter2: t('breadcrumb_eat_better'),
          chapter3: t('breadcrumb_fam'),
          filtre: Object.keys(filters)
            .map((key) => {
              if (filters[key] !== '' && filters[key].length > 0) {
                return tcRecipeFilterTitle[
                  key as keyof typeof tcRecipeFilterTitle
                ]
              }
              return ''
            })
            .filter((v) => v !== '')
            .join(', '),
          click: Object.values(filters)
            .map((v) => {
              if (Array.isArray(v)) {
                return v.join('|')
              } else if (typeof v === 'string' && v !== '') {
                return v
              }
            })
            .filter((v) => v !== '')
            .join(', '),
        })
    },
    [currentTcTemplate, dispatch, filters, t]
  )

  const handleCloseFilter = useCallback(
    (ev?: SyntheticEvent) => {
      setFilters(storeFilters)
      ev &&
        tcClick({
          click_name: `picto fermer filtre_${currentTcTemplate}_fam`,
          click_type: TcClickTypes.action,
          chapter1: `action_${currentTcTemplate}_fam`,
          chapter2: t('breadcrumb_eat_better'),
          chapter3: t('breadcrumb_fam'),
        })
    },
    [currentTcTemplate, storeFilters, t]
  )

  useEffect(() => {
    const params = stringify(
      {
        page: currentPage,
        ...storeFilters,
      },
      { arrayFormat: 'comma', skipNull: true, skipEmptyString: true }
    )

    Router.replaceRoute(`${routes.recipes.path}?${params}`, {
      shallow: true,
      scroll: false,
    })
  }, [currentPage, handleApplyFilter, queryParameter, storeFilters])

  useEffect(() => {
    if (lastRecipeFilteredQuery.current !== filters.query) {
      lastRecipeFilteredQuery.current = filters
      handleApplyFilter()
    }
  }, [filters, handleApplyFilter])

  const loadMoreHandler = useCallback(() => {
    dispatch(RecipesActions?.loadMoreRecipesPage())
  }, [dispatch])
  const loadPreviousHandler = useCallback(() => {
    dispatch(RecipesActions?.loadPreviousRecipesPage())
  }, [dispatch])

  const breadcrumbName = t('breadcrumb_recipes')

  const breadcrumbConfig = breadcrumbTransformer({
    bgColor: colors.greenMint,
    hasFamItem: false,
    t,
    lastItems: [
      {
        text: breadcrumbName,
      },
    ],
  })

  const recipesTemplateProps: RecipesTemplateProps = useMemo(
    () => ({
      coverSectionProps: recipesCoverTransformer({
        t,
        breadcrumbItems: breadcrumbConfig,
        onSearch: setQuery,
        backgroundImageDesktop: settings?.covers?.recipes_list?.desktop ?? '',
        backgroundImageMobile: settings?.covers?.recipes_list?.mobile ?? '',
        backgroundImageAlt: settings?.covers?.recipes_list?.alt ?? '',
        initialValue: filters.query,
      }),
      recipesHighlightSectionProps: recipesHighlightTransformer(
        settings?.featured ?? [],
        isMobile,
        t
      ),
      recipesListSectionProps: recipesListTransformer({
        t,
        filters,
        handleFilterChange,
        recipes: recipes ?? [],
        loadMoreFunction: hasMorePages ? loadMoreHandler : undefined,
        loadPreviousFunction: hasPreviousPages
          ? loadPreviousHandler
          : undefined,
        handleApplyFilter,
        handleCloseFilter,
        ignoredFilters: [],
        tcVars: {
          chapter2: t('breadcrumb_home'),
          chapter3: t('breadcrumb_eat_better'),
        },
        resultsRef,
      }),
      seoProps: recipesSeoTransformer(t),
      mobileBreadcrumbProps: breadcrumbConfig,
      tcProps: tagCommanderTransformer({
        pageName: breadcrumbName,
        tcTemplate: currentTcTemplate,
        tcCAt1: t('tracking_page_chapter_1'),
        tcCAt2: t('tracking_page_chapter_2'),
        tcCAt3: t('tracking_page_chapter_3'),
        tcCAt4: t('tracking_page_chapter_4_recipes'),
        filters: storeFilters,
        searchQuery: filters.query,
      }),
    }),
    [
      t,
      breadcrumbConfig,
      setQuery,
      settings?.covers?.recipes_list?.desktop,
      settings?.covers?.recipes_list?.mobile,
      settings?.covers?.recipes_list?.alt,
      settings?.featured,
      isMobile,
      filters,
      handleFilterChange,
      recipes,
      handleApplyFilter,
      handleCloseFilter,
      loadMoreHandler,
      hasMorePages,
      loadPreviousHandler,
      hasPreviousPages,
      breadcrumbName,
      currentTcTemplate,
      storeFilters,
    ]
  )

  return { recipesTemplateProps }
}

export default useRecipes
